import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '../supabase'
import { useCurrency } from '../context/CurrencyContext'

function calcMonths(balance, emi, annualRate) {
  if (!balance || !emi || emi <= 0) return null
  if (!annualRate) return Math.ceil(balance / emi)
  const r = annualRate / 12 / 100
  if (emi <= r * balance) return null
  return Math.ceil(-Math.log(1 - (r * balance) / emi) / Math.log(1 + r))
}

function addMonths(date, n) {
  const d = new Date(date)
  d.setMonth(d.getMonth() + n)
  return d.toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

function round50(n) {
  return Math.max(0, Math.round(n / 50) * 50)
}

function todayStr() {
  return new Date().toISOString().slice(0, 10)
}

const tabBtnStyle = (active) => ({
  flex: 1,
  padding: '0.55rem 0.6rem',
  borderRadius: '6px',
  border: '1.5px solid',
  borderColor: active ? '#c9a84c' : 'var(--border)',
  background: active ? 'rgba(201,168,76,0.1)' : 'transparent',
  color: active ? '#c9a84c' : 'var(--text-muted)',
  fontFamily: 'monospace',
  fontSize: '0.7rem',
  fontWeight: '600',
  letterSpacing: '0.03em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  transition: 'all 0.15s',
})

const labelStyle = {
  display: 'block', fontFamily: 'monospace', fontSize: '0.67rem', fontWeight: '500',
  textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.35rem',
}

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: '4px',
  padding: '0.55rem 0.75rem', fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box', outline: 'none',
}

const primaryBtnStyle = (disabled) => ({
  background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.6rem 1.2rem',
  borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500',
  cursor: disabled ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em',
  opacity: disabled ? 0.6 : 1, width: '100%',
})

function StatBox({ label, current, improved, color }) {
  return (
    <div style={{ background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.9rem 1rem' }}>
      <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{label}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>Current</div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: 'var(--text)' }}>{current}</div>
        </div>
        {improved && (
          <>
            <div style={{ color: 'var(--border)', fontSize: '1rem' }}>→</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginBottom: '0.15rem' }}>With extra</div>
              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700', color: color || '#4a6741' }}>{improved}</div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default function ExtraPaymentModal({ loan, userId, onClose, onUpdated }) {
  const { fmt } = useCurrency()

  const [localLoan, setLocalLoan] = useState(loan)
  const [tab, setTab] = useState('one_time') // 'one_time' | 'recurring'

  const [oneTimeAmt, setOneTimeAmt] = useState('')
  const [oneTimeDate, setOneTimeDate] = useState(todayStr())
  const [recurringAmt, setRecurringAmt] = useState('')

  const [history, setHistory] = useState([])
  const [activeRecurring, setActiveRecurring] = useState(null)
  const [loadingData, setLoadingData] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const balance = parseFloat(localLoan.balance) || 0
  const emi = parseFloat(localLoan.emi_amount) || 0
  const rate = parseFloat(localLoan.interest_rate) || 0
  const existingExtraEmi = parseFloat(localLoan.extra_emi_amount) || 0
  const effectiveEmi = emi + existingExtraEmi
  const today = new Date()

  const loadData = useCallback(async () => {
    setLoadingData(true)
    const { data, error } = await supabase
      .from('loan_extra_payments')
      .select('*')
      .eq('loan_id', localLoan.id)
      .order('payment_date', { ascending: false })
    if (!error && data) {
      setHistory(data.filter(r => r.type === 'one_time'))
      setActiveRecurring(data.find(r => r.type === 'recurring' && r.is_active) || null)
    }
    setLoadingData(false)
  }, [localLoan.id])

  useEffect(() => { loadData() }, [loadData])

  function notifyUpdated(patch) {
    const updated = { ...localLoan, ...patch }
    setLocalLoan(updated)
    if (onUpdated) onUpdated(updated)
  }

  // ---------------- Suggestion engine ----------------
  // Compares a sensible default one-time lump sum against a sensible default
  // recurring top-up, using "interest saved per rupee committed" as the
  // fairness metric between a single payment and an ongoing monthly one.
  const suggestion = useMemo(() => {
    if (!balance || !effectiveEmi || balance <= 0) return null
    const baseMonths = calcMonths(balance, effectiveEmi, rate)
    if (!baseMonths) return null
    const baseInterest = effectiveEmi * baseMonths - balance

    const suggestedOneTime = Math.min(round50(emi * 3) || 500, balance)
    const suggestedRecurring = round50(emi * 0.2) || 50

    const otMonths = calcMonths(balance - suggestedOneTime, effectiveEmi, rate)
    const otInterest = otMonths != null ? effectiveEmi * otMonths - (balance - suggestedOneTime) : null
    const otInterestSaved = otInterest != null ? baseInterest - otInterest : 0
    const otEfficiency = suggestedOneTime > 0 ? otInterestSaved / suggestedOneTime : 0

    const rMonths = calcMonths(balance, effectiveEmi + suggestedRecurring, rate)
    const rInterest = rMonths != null ? (effectiveEmi + suggestedRecurring) * rMonths - balance : null
    const rInterestSaved = rInterest != null ? baseInterest - rInterest : 0
    const rTotalOutlay = rMonths != null ? suggestedRecurring * rMonths : 0
    const rEfficiency = rTotalOutlay > 0 ? rInterestSaved / rTotalOutlay : 0

    return {
      suggestedOneTime, suggestedRecurring,
      otInterestSaved, rInterestSaved,
      otMonthsSaved: otMonths != null ? baseMonths - otMonths : 0,
      rMonthsSaved: rMonths != null ? baseMonths - rMonths : 0,
      recommend: rEfficiency >= otEfficiency ? 'recurring' : 'one_time',
    }
  }, [balance, emi, effectiveEmi, rate])

  function applySuggestion() {
    if (!suggestion) return
    if (suggestion.recommend === 'one_time') {
      setTab('one_time'); setOneTimeAmt(String(suggestion.suggestedOneTime))
    } else {
      setTab('recurring'); setRecurringAmt(String(suggestion.suggestedRecurring))
    }
  }

  // ---------------- One-time tab impact ----------------
  const otAmt = parseFloat(oneTimeAmt) || 0
  const baseMonths = useMemo(() => calcMonths(balance, effectiveEmi, rate), [balance, effectiveEmi, rate])
  const otNewMonths = useMemo(() => otAmt > 0 ? calcMonths(Math.max(balance - otAmt, 0), effectiveEmi, rate) : null, [otAmt, balance, effectiveEmi, rate])
  const otMonthsSaved = (baseMonths != null && otNewMonths != null) ? baseMonths - otNewMonths : null
  const baseInterest = baseMonths != null ? effectiveEmi * baseMonths - balance : null
  const otNewInterest = otNewMonths != null ? effectiveEmi * otNewMonths - Math.max(balance - otAmt, 0) : null
  const otInterestSaved = (baseInterest != null && otNewInterest != null) ? baseInterest - otNewInterest : null

  // ---------------- Recurring tab impact ----------------
  const rAmt = parseFloat(recurringAmt) || 0
  const rNewMonths = useMemo(() => rAmt > 0 ? calcMonths(balance, effectiveEmi + rAmt, rate) : null, [rAmt, balance, effectiveEmi, rate])
  const rMonthsSaved = (baseMonths != null && rNewMonths != null) ? baseMonths - rNewMonths : null
  const rNewInterest = rNewMonths != null ? (effectiveEmi + rAmt) * rNewMonths - balance : null
  const rInterestSaved = (baseInterest != null && rNewInterest != null) ? baseInterest - rNewInterest : null

  async function handleLogOneTime() {
    if (!otAmt || otAmt <= 0) { setError('Enter a valid amount'); return }
    if (otAmt > balance) { setError('Amount can\'t exceed the current balance'); return }
    setSaving(true); setError(''); setSuccess('')
    const { data, error } = await supabase.rpc('apply_one_time_extra_payment', {
      p_loan_id: localLoan.id, p_amount: otAmt, p_payment_date: oneTimeDate, p_note: null,
    })
    if (error) { setError('Could not log payment: ' + error.message); setSaving(false); return }
    const updatedLoan = Array.isArray(data) ? data[0] : data
    if (updatedLoan) notifyUpdated(updatedLoan)
    setOneTimeAmt('')
    setSuccess(`Logged ${fmt(otAmt)} extra payment — balance updated.`)
    await loadData()
    setSaving(false)
  }

  async function handleStartRecurring() {
    if (!rAmt || rAmt <= 0) { setError('Enter a valid amount'); return }
    setSaving(true); setError(''); setSuccess('')
    const { error } = await supabase.from('loan_extra_payments').insert({
      loan_id: localLoan.id, user_id: userId, type: 'recurring', amount: rAmt,
      payment_date: todayStr(), is_active: true,
    })
    if (error) { setError('Could not save: ' + error.message); setSaving(false); return }
    notifyUpdated({ extra_emi_amount: existingExtraEmi + rAmt })
    setRecurringAmt('')
    setSuccess(`Recurring extra of ${fmt(rAmt)}/mo activated.`)
    await loadData()
    setSaving(false)
  }

  async function handleStopRecurring() {
    if (!activeRecurring) return
    if (!window.confirm('Stop this recurring extra payment? Future EMIs will go back to the base amount.')) return
    setSaving(true); setError('')
    const { error } = await supabase.from('loan_extra_payments').update({ is_active: false }).eq('id', activeRecurring.id)
    if (error) { setError('Could not stop: ' + error.message); setSaving(false); return }
    notifyUpdated({ extra_emi_amount: Math.max(existingExtraEmi - parseFloat(activeRecurring.amount), 0) })
    setActiveRecurring(null)
    setSuccess('Recurring extra payment stopped.')
    setSaving(false)
  }

  const isPaidOff = balance <= 0

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{ position: 'fixed', inset: 0, background: 'var(--modal-backdrop)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'var(--surface)', borderRadius: '10px', border: '1px solid var(--border)', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid var(--border)', background: 'var(--surface-hdr)' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700' }}>Extra Payment</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>{localLoan.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

          {isPaidOff ? (
            <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#4a6741' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎉</div>
              <div style={{ fontFamily: 'Georgia,serif', fontWeight: '700' }}>This loan is fully paid off!</div>
            </div>
          ) : (
            <>
              {error && (
                <div style={{ background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.65rem 0.9rem', fontSize: '0.78rem', color: '#b8441a', fontFamily: 'monospace' }}>
                  ⚠ {error}
                </div>
              )}
              {success && (
                <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '6px', padding: '0.65rem 0.9rem', fontSize: '0.78rem', color: '#4a6741', fontFamily: 'monospace' }}>
                  ✓ {success}
                </div>
              )}

              {existingExtraEmi > 0 && (
                <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.5rem 0.75rem' }}>
                  Effective EMI right now: {fmt(emi)} base + {fmt(existingExtraEmi)} recurring extra = <strong style={{ color: 'var(--text)' }}>{fmt(effectiveEmi)}/mo</strong>
                </div>
              )}

              {/* Suggestion banner */}
              {suggestion && (
                <div style={{ background: 'rgba(201,168,76,0.07)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '8px', padding: '0.9rem 1.1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: '700', color: '#c9a84c', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.4rem' }}>
                        💡 Suggested
                      </div>
                      {suggestion.recommend === 'recurring' ? (
                        <div style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>
                          A <strong>recurring extra of {fmt(suggestion.suggestedRecurring)}/mo</strong> saves more interest per rupee than a one-time payment here — about <strong style={{ color: '#4a6741' }}>{fmt(suggestion.rInterestSaved)}</strong> saved and <strong style={{ color: '#4a6741' }}>{suggestion.rMonthsSaved} months</strong> off the term if kept up.
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.5 }}>
                          A <strong>one-time payment of {fmt(suggestion.suggestedOneTime)}</strong> right now is the more efficient move — about <strong style={{ color: '#4a6741' }}>{fmt(suggestion.otInterestSaved)}</strong> saved and <strong style={{ color: '#4a6741' }}>{suggestion.otMonthsSaved} months</strong> off the term.
                        </div>
                      )}
                    </div>
                  </div>
                  <button onClick={applySuggestion} style={{ marginTop: '0.7rem', background: 'transparent', border: '1px solid #c9a84c', color: '#c9a84c', padding: '0.32rem 0.75rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.65rem', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.03em' }}>
                    Use this suggestion →
                  </button>
                </div>
              )}

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button style={tabBtnStyle(tab === 'one_time')} onClick={() => setTab('one_time')}>One-Time Payment</button>
                <button style={tabBtnStyle(tab === 'recurring')} onClick={() => setTab('recurring')}>Recurring Extra</button>
              </div>

              {/* ---------------- One-time tab ---------------- */}
              {tab === 'one_time' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                      <label style={labelStyle}>Lump Sum Amount (₹)</label>
                      <input style={inputStyle} type="text" inputMode="decimal" placeholder="e.g. 25000" value={oneTimeAmt} onChange={e => setOneTimeAmt(e.target.value)} />
                    </div>
                    <div>
                      <label style={labelStyle}>Payment Date</label>
                      <input style={inputStyle} type="date" value={oneTimeDate} onChange={e => setOneTimeDate(e.target.value)} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <StatBox label="Payoff Date" current={baseMonths ? addMonths(today, baseMonths) : 'N/A'} improved={otNewMonths ? addMonths(today, otNewMonths) : null} />
                    <StatBox label="Months Remaining" current={baseMonths ? baseMonths + ' mo' : 'N/A'} improved={otNewMonths ? otNewMonths + ' mo' : null} />
                    <StatBox label="Total Interest" current={baseInterest != null ? fmt(baseInterest) : 'N/A'} improved={otNewInterest != null ? fmt(otNewInterest) : null} color="#4a6741" />
                    <StatBox label="New Balance" current={fmt(balance)} improved={otAmt > 0 ? fmt(Math.max(balance - otAmt, 0)) : null} color="#4a6741" />
                  </div>

                  {otMonthsSaved != null && otMonthsSaved > 0 && (
                    <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '8px', padding: '1rem 1.2rem' }}>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4a6741', marginBottom: '0.5rem' }}>💰 Savings Summary</div>
                      <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: '#4a6741' }}>{otMonthsSaved} months</div>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741' }}>saved off your term</div>
                        </div>
                        {otInterestSaved != null && otInterestSaved > 0 && (
                          <div>
                            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: '#4a6741' }}>{fmt(otInterestSaved)}</div>
                            <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741' }}>interest saved</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <button onClick={handleLogOneTime} disabled={saving || !otAmt} style={primaryBtnStyle(saving || !otAmt)}>
                    {saving ? 'Logging...' : 'Log This Payment & Reduce Balance'}
                  </button>

                  {!loadingData && history.length > 0 && (
                    <div>
                      <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                        Past One-Time Payments
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '140px', overflowY: 'auto' }}>
                        {history.map(h => (
                          <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', fontSize: '0.7rem', background: 'var(--surface-hdr)', padding: '0.45rem 0.7rem', borderRadius: '4px' }}>
                            <span style={{ color: 'var(--text-muted)' }}>{new Date(h.payment_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                            <span style={{ color: 'var(--text)', fontWeight: '600' }}>{fmt(h.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ---------------- Recurring tab ---------------- */}
              {tab === 'recurring' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {activeRecurring ? (
                    <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '8px', padding: '1rem 1.2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                      <div>
                        <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4a6741', marginBottom: '0.3rem' }}>Active since {new Date(activeRecurring.payment_date).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}</div>
                        <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '700', color: '#4a6741' }}>{fmt(activeRecurring.amount)}/mo</div>
                      </div>
                      <button onClick={handleStopRecurring} disabled={saving} style={{ background: 'transparent', border: '1px solid rgba(184,68,26,0.4)', color: '#b8441a', padding: '0.4rem 0.8rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.68rem', fontWeight: '600', cursor: 'pointer' }}>
                        Stop
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label style={labelStyle}>Extra Amount Added to Every EMI (₹)</label>
                        <input style={inputStyle} type="text" inputMode="decimal" placeholder="e.g. 2000" value={recurringAmt} onChange={e => setRecurringAmt(e.target.value)} />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <StatBox label="Payoff Date" current={baseMonths ? addMonths(today, baseMonths) : 'N/A'} improved={rNewMonths ? addMonths(today, rNewMonths) : null} />
                        <StatBox label="Months Remaining" current={baseMonths ? baseMonths + ' mo' : 'N/A'} improved={rNewMonths ? rNewMonths + ' mo' : null} />
                        <StatBox label="Total Interest" current={baseInterest != null ? fmt(baseInterest) : 'N/A'} improved={rNewInterest != null ? fmt(rNewInterest) : null} color="#4a6741" />
                        <StatBox label="New Monthly EMI" current={fmt(effectiveEmi)} improved={rAmt > 0 ? fmt(effectiveEmi + rAmt) : null} color="#4a6741" />
                      </div>

                      {rMonthsSaved != null && rMonthsSaved > 0 && (
                        <div style={{ background: 'rgba(74,103,65,0.08)', border: '1px solid rgba(74,103,65,0.25)', borderRadius: '8px', padding: '1rem 1.2rem' }}>
                          <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#4a6741', marginBottom: '0.5rem' }}>💰 Savings Summary (if kept up)</div>
                          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: '#4a6741' }}>{rMonthsSaved} months</div>
                              <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741' }}>saved off your term</div>
                            </div>
                            {rInterestSaved != null && rInterestSaved > 0 && (
                              <div>
                                <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: '#4a6741' }}>{fmt(rInterestSaved)}</div>
                                <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#4a6741' }}>interest saved</div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <button onClick={handleStartRecurring} disabled={saving || !rAmt} style={primaryBtnStyle(saving || !rAmt)}>
                        {saving ? 'Activating...' : 'Start Adding This to Every EMI'}
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: '0.75rem 1.5rem 1.2rem', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ background: 'var(--btn-bg)', color: 'var(--btn-text)', border: 'none', padding: '0.5rem 1.2rem', borderRadius: '4px', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: '500', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
